import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
  createContext,
} from "react";
import { loadSpace, loadEmbed } from "@usersnap/browser";
import { getInfo as getDatadogInfo } from "@labxchange/util-datadog";

const USERSNAP_SPACE_API_KEY = process.env.REACT_APP_USERSNAP_SPACE_API_KEY;

export const UsersnapContext = createContext({
  usersnapApi: null,
  isActive: false,
  loadUsersnap: async () => null,
});

export const UsersnapProvider = ({
  initParams = null,
  onLoadCallback,
  children,
  lazy = false,
}) => {
  const [usersnapApi, setUsersnapApi] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const isInitializingRef = useRef(false);
  const onLoadCallbackRef = useRef(onLoadCallback);

  // Keep onLoadCallback ref in sync without causing re-renders
  useEffect(() => {
    onLoadCallbackRef.current = onLoadCallback;
  }, [onLoadCallback]);

  const initializeUsersnap = useCallback(async () => {
    if (usersnapApi || isInitializingRef.current) return usersnapApi;

    if (!USERSNAP_SPACE_API_KEY) {
      console.error("USERSNAP_SPACE_API_KEY is missing");
      return null;
    }

    isInitializingRef.current = true;
    console.info("initializing usersnap");

    try {
      const api = await loadSpace(USERSNAP_SPACE_API_KEY);

      api.init({ init: initParams, custom: getDatadogInfo() });

      api.on("open", () => setIsActive(true));
      api.on("close", () => setIsActive(false));
      api.on("load", (event) => onLoadCallbackRef.current?.(event, api));

      setUsersnapApi(api);
      return api;
    } catch (error) {
      console.error("Failed to load Usersnap", error);
      isInitializingRef.current = false;
      return null;
    }
  }, [initParams, usersnapApi]);

  // Default behavior: load on mount if not lazy
  useEffect(() => {
    if (!lazy) {
      initializeUsersnap();
    }
  }, [lazy, initializeUsersnap]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      usersnapApi?.destroy();
    };
  }, [usersnapApi]);

  return (
    <UsersnapContext.Provider
      value={{
        usersnapApi,
        isActive,
        loadUsersnap: initializeUsersnap,
      }}
    >
      {children}
    </UsersnapContext.Provider>
  );
};

// Enhanced hook with lazy loading support
export const useUsersnap = () => {
  const context = useContext(UsersnapContext);
  if (!context) {
    throw new Error("useUsersnap must be used within a UsersnapProvider");
  }

  return {
    api: context.usersnapApi,
    isActive: context.isActive,
    loadUsersnap: context.loadUsersnap,
    ensureLoaded: async () => {
      if (context.usersnapApi) return context.usersnapApi;
      return await context.loadUsersnap();
    },
  };
};

export function usersnapLoadEmbededWidget(element) {
  if (process.env.REACT_APP_USERSNAP_SCROLLABLE_FEEDBACK_MENU) {
    loadEmbed(process.env.REACT_APP_USERSNAP_SCROLLABLE_FEEDBACK_MENU).then(
      (api) => {
        api.init({
          mountNode: element,
        });
      }
    );
  }
}

export function useUsersnapApi() {
  return useContext(UsersnapContext).usersnapApi;
}

export function isUsersnapActive() {
  return useContext(UsersnapContext).isActive;
}
